import React from "react";
import type { Brand } from "../api";

export type HeaderView =
  | "gallery"
  | "ideas"
  | "plan"
  | "storyboards"
  | "composer"
  | "workspace"
  | "queue"
  | "deploy"
  | "capture"
  | "users"
  | "editor";

type Props = {
  brand: Brand | null;
  view: HeaderView;
  setView: (v: HeaderView) => void;
  queueCount: number;
  workspaceCount?: number;
  ideaPoolCount?: number;
  storyboardCount?: number;
  planCount?: number;
  deployCount?: number;
  /** Anzahl der laufenden / wartenden Renders. >0 → Queue-Tab pulsiert. */
  activeRenderCount?: number;
  /** Mittlerer Fortschritt der aktiven Renders (0..1). null = keiner aktiv. */
  activeRenderProgress?: number | null;
  /** Nur gesetzt, wenn ein Login aktiv ist → zeigt den Abmelden-Button. */
  onLogout?: () => void;
  /** Aktueller Benutzername (für Header-Anzeige). */
  currentUser?: string | null;
  /** Admin → Benutzerverwaltungs-Tab sichtbar. */
  isAdmin?: boolean;
};

export const Header: React.FC<Props> = ({
  brand,
  view,
  setView,
  queueCount,
  workspaceCount,
  ideaPoolCount,
  storyboardCount,
  planCount,
  deployCount,
  activeRenderCount = 0,
  activeRenderProgress = null,
  onLogout,
  currentUser,
  isAdmin = false,
}) => {
  const rendering = activeRenderCount > 0;
  const pct =
    activeRenderProgress !== null && Number.isFinite(activeRenderProgress)
      ? Math.max(0, Math.min(100, Math.round(activeRenderProgress * 100)))
      : null;
  const counter = (n?: number): React.ReactNode =>
    n && n > 0 ? <span style={{ marginLeft: 6, opacity: 0.9 }}>· {n}</span> : null;
  return (
    <header className="header">
      <div className="header-brand">
        {brand?.logo ? (
          <img src={`/brand/${brand.logo.split("/").pop()}`} alt={brand.name} />
        ) : null}
        <div className="name">
          Marketing <strong>Studio</strong>
        </div>
      </div>
      <nav className="tabs">
        <button className={`tab ${view === "gallery" ? "active" : ""}`} onClick={() => setView("gallery")}>
          Ideen-Galerie
        </button>
        <button
          className={`tab ${view === "ideas" ? "active" : ""}`}
          onClick={() => setView("ideas")}
          title="Dynamischer Ideen-Pool (KI + Recherche + Approval)"
        >
          Ideen-Pool {counter(ideaPoolCount)}
        </button>
        <button
          className={`tab ${view === "plan" ? "active" : ""}`}
          onClick={() => setView("plan")}
          title="Redaktions-Kalender"
        >
          Planung {counter(planCount)}
        </button>
        <button
          className={`tab ${view === "storyboards" ? "active" : ""}`}
          onClick={() => setView("storyboards")}
          title="Storyboards (Idee → Beats → Composer)"
        >
          Storyboard {counter(storyboardCount)}
        </button>
        <span aria-hidden style={{ width: 1, alignSelf: "stretch", background: "rgba(255,255,255,0.12)", margin: "0 6px" }} />
        <button className={`tab ${view === "composer" ? "active" : ""}`} onClick={() => setView("composer")}>
          Composer
        </button>
        <button className={`tab ${view === "workspace" ? "active" : ""}`} onClick={() => setView("workspace")}>
          Workspace {counter(workspaceCount)}
        </button>
        <button
          className={`tab ${view === "queue" ? "active" : ""} ${rendering ? "is-rendering" : ""}`}
          onClick={() => setView("queue")}
          title={
            rendering
              ? `${activeRenderCount} Render${activeRenderCount === 1 ? "" : "s"} läuft im Hintergrund${pct !== null ? ` · ${pct}%` : ""}`
              : "Render-Warteschlange"
          }
        >
          {rendering ? <span className="render-pulse" aria-hidden /> : null}
          Queue {counter(queueCount)}
          {rendering ? (
            <span className="render-chip" aria-live="polite">
              {pct !== null ? `${pct}%` : "…"}
            </span>
          ) : null}
        </button>
        <button
          className={`tab ${view === "deploy" ? "active" : ""}`}
          onClick={() => setView("deploy")}
          title="Deployment auf Social-Media"
        >
          Deployment {counter(deployCount)}
        </button>
        <button
          className={`tab ${view === "capture" ? "active" : ""}`}
          onClick={() => setView("capture")}
          title="Eigene Screenshots & Screen-Recordings aufnehmen (auch manuell mit der Maus)"
        >
          Capture
        </button>
        {isAdmin ? (
          <button
            className={`tab ${view === "users" ? "active" : ""}`}
            onClick={() => setView("users")}
            title="Benutzer anlegen, aktivieren/deaktivieren, Passwörter setzen"
          >
            Benutzer
          </button>
        ) : null}
        <span aria-hidden style={{ width: 1, alignSelf: "stretch", background: "rgba(255,255,255,0.12)", margin: "0 6px" }} />
        <button
          className={`tab subtle ${view === "editor" ? "active" : ""}`}
          onClick={() => setView("editor")}
          title="Klassischer Single-Template-Editor"
        >
          Editor (legacy)
        </button>
      </nav>
      <div className="header-meta">
        {brand ? (
          <>
            <span className="badge">{brand.name}</span>
            <a href={brand.url} target="_blank" rel="noreferrer">
              {brand.url.replace("https://", "")}
            </a>
          </>
        ) : (
          <span>laden…</span>
        )}
        {onLogout ? (
          <>
            {currentUser ? <span className="header-user" title="Angemeldet als">{currentUser}</span> : null}
            <button className="header-logout" onClick={onLogout} title="Abmelden">
              Abmelden
            </button>
          </>
        ) : null}
      </div>
    </header>
  );
};
